import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { UniversalCamera } from '@babylonjs/core/Cameras/universalCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { PointLight } from '@babylonjs/core/Lights/pointLight';
import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import '@babylonjs/core/Rendering/edgesRenderer';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { GlowLayer } from '@babylonjs/core/Layers/glowLayer';
import '@babylonjs/core/Layers/effectLayerSceneComponent';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';

import { PALETTE } from '@observant/shared';
import type { CatRoomState } from '../sim/catroom.js';
import { DEFAULT_CATROOM_CONFIG as ROOM } from '../sim/catroom.js';
import { createQubitMesh, type QubitMesh } from './qubitMesh.js';

function edge(mesh: Mesh, hex: string, width = 2): void {
  const c = Color3.FromHexString(hex);
  mesh.enableEdgesRendering();
  mesh.edgesWidth = width;
  mesh.edgesColor = new Color4(c.r, c.g, c.b, 1);
}

/** A flat lit text sign (dark text on a colored panel, or glowing text). */
function sign(
  scene: Scene,
  text: string,
  bg: string,
  fg: string,
  w: number,
  h: number,
  name: string,
): Mesh {
  const tex = new DynamicTexture(`tex-${name}`, { width: 512, height: 256 }, scene, false);
  const ctx = tex.getContext() as unknown as CanvasRenderingContext2D;
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, 512, 256);
  ctx.textAlign = 'center';
  ctx.fillStyle = fg;
  let fontPx = 150;
  ctx.font = `bold ${fontPx}px monospace`;
  while (ctx.measureText(text).width > 480 && fontPx > 22) {
    fontPx -= 6;
    ctx.font = `bold ${fontPx}px monospace`;
  }
  ctx.fillText(text, 256, 128 + fontPx * 0.28);
  tex.update();
  // Signs are rotated 180° to face the camera, so flip U to read correctly.
  tex.uScale = -1;
  tex.uOffset = 1;
  const mat = new StandardMaterial(`signmat-${name}`, scene);
  mat.emissiveTexture = tex;
  mat.diffuseColor = new Color3(0, 0, 0);
  mat.disableLighting = true;
  mat.backFaceCulling = false; // visible from either side
  const plane = CreatePlane(name, { width: w, height: h }, scene);
  plane.material = mat;
  return plane;
}

export class CatRoomScene {
  readonly engine: Engine;
  readonly scene: Scene;
  private camera: UniversalCamera;
  private qubit: QubitMesh;
  private button: Mesh;
  private buttonMat: StandardMaterial;
  private peekX = 0;
  private peekY = 0;

  constructor(engine: Engine) {
    this.engine = engine;
    this.scene = new Scene(this.engine);
    this.scene.clearColor = new Color4(0.006, 0.008, 0.014, 1);

    // Start inside the room (the chase camera is clamped to stay in-bounds).
    this.camera = new UniversalCamera('cam', new Vector3(0, 4.2, 1), this.scene);
    this.camera.fov = 0.95;
    this.camera.minZ = 0.1;
    this.camera.maxZ = 200;

    const glow = new GlowLayer('glow', this.scene, { blurKernelSize: 24 });
    glow.intensity = 0.7;

    // Dim room, with a dramatic light over the box.
    const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), this.scene);
    hemi.intensity = 0.32;
    hemi.diffuse = new Color3(0.6, 0.7, 0.9);
    const spot = new PointLight('spot', new Vector3(0, 9, ROOM.box.z), this.scene);
    spot.intensity = 0.9;
    spot.diffuse = new Color3(1, 0.95, 0.85);

    const dark = new StandardMaterial('wall', this.scene);
    dark.diffuseColor = new Color3(0.05, 0.06, 0.09);
    dark.emissiveColor = new Color3(0.01, 0.015, 0.025);

    const w = ROOM.xMax - ROOM.xMin;
    const d = ROOM.zMax - ROOM.zMin;
    const cz = (ROOM.zMin + ROOM.zMax) / 2;
    const CEIL = 10;

    const floor = CreateBox('floor', { width: w, height: 0.4, depth: d }, this.scene);
    floor.position.set(0, -0.2, cz);
    floor.material = dark;
    edge(floor, PALETTE.neonCyan, 1);

    const mkWall = (x: number, z: number, ww: number, wd: number, n: string) => {
      const wall = CreateBox(n, { width: ww, height: CEIL, depth: wd }, this.scene);
      wall.position.set(x, CEIL / 2, z);
      wall.material = dark;
      edge(wall, PALETTE.neonCyan, 1);
    };
    mkWall(ROOM.xMin, cz, 0.4, d, 'wallL');
    mkWall(ROOM.xMax, cz, 0.4, d, 'wallR');
    mkWall(0, ROOM.zMin, w, 0.4, 'wallBack');
    mkWall(0, ROOM.zMax, w, 0.4, 'wallFront');

    // Schrödinger's box.
    const box = CreateBox(
      'box',
      { width: ROOM.box.hx * 2, height: 2, depth: ROOM.box.hz * 2 },
      this.scene,
    );
    box.position.set(ROOM.box.x, 1, ROOM.box.z);
    const wood = new StandardMaterial('wood', this.scene);
    wood.diffuseColor = new Color3(0.32, 0.2, 0.09);
    wood.emissiveColor = new Color3(0.06, 0.035, 0.015);
    box.material = wood;
    edge(box, '#ffb877', 2);
    const label = sign(
      this.scene,
      "SCHRÖDINGER'S CAT",
      '#1a0f06',
      '#ffd9a0',
      ROOM.box.hx * 2 * 0.96,
      1.0,
      'catlabel',
    );
    label.position.set(ROOM.box.x, 1.05, ROOM.box.z - ROOM.box.hz - 0.15);
    label.rotation.y = Math.PI; // face toward the approaching player (−Z)

    // Red mushroom OPEN button on the front of the box.
    this.buttonMat = new StandardMaterial('btn', this.scene);
    this.buttonMat.emissiveColor = new Color3(1, 0.1, 0.12);
    this.buttonMat.diffuseColor = new Color3(0.2, 0, 0);
    const base = CreateCylinder('btnbase', { diameter: 0.5, height: 0.4 }, this.scene);
    base.position.set(0, 2.2, ROOM.box.z - ROOM.box.hz + 0.1);
    base.material = dark;
    this.button = CreateSphere('btn', { diameter: 0.9, segments: 16 }, this.scene);
    this.button.position.set(0, 2.5, ROOM.box.z - ROOM.box.hz + 0.1);
    this.button.scaling.y = 0.6;
    this.button.material = this.buttonMat;
    const openSign = sign(this.scene, 'OPEN', '#f5c400', '#101010', 1.4, 0.6, 'opensign');
    openSign.position.set(0, 3.2, ROOM.box.z - ROOM.box.hz - 0.15);
    openSign.rotation.y = Math.PI;

    // EXIT door at the far wall.
    const frame = CreateBox('exitframe', { width: 2.4, height: 3.4, depth: 0.3 }, this.scene);
    frame.position.set(ROOM.exit.x, 1.7, ROOM.zMax - 0.3);
    const exitMat = new StandardMaterial('exitmat', this.scene);
    exitMat.emissiveColor = new Color3(0.1, 1, 0.45);
    exitMat.disableLighting = true;
    frame.material = exitMat;
    frame.visibility = 0.5;
    edge(frame, '#19f7a0', 3);
    const exitSign = sign(this.scene, 'EXIT', '#06140b', '#19f7a0', 2, 0.8, 'exitsign');
    exitSign.position.set(ROOM.exit.x, 3.0, ROOM.zMax - 0.45);
    exitSign.rotation.y = Math.PI;

    this.qubit = createQubitMesh(this.scene);

    this.onResize = this.onResize.bind(this);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
  }

  private onResize(): void {
    this.engine.resize();
  }

  /** Right-stick look/peek (render-only). */
  setLook(x: number, y: number): void {
    this.peekX = x;
    this.peekY = y;
  }

  sync(state: CatRoomState): void {
    this.qubit.root.position.set(state.pos.x, 0.9, state.pos.z);
    this.qubit.root.rotation.y = state.facing;
    this.qubit.tick(state.t);

    // Chase camera, trailing the player but CLAMPED to stay inside the room
    // (so it never slips behind the back wall). Right stick peeks.
    const cam = this.camera.position;
    const tx = state.pos.x * 0.4 + this.peekX * 3;
    const tz = Math.max(ROOM.zMin + 0.6, state.pos.z - 4);
    cam.x += (tx - cam.x) * 0.12;
    cam.z += (tz - cam.z) * 0.12;
    cam.y = 4.2;
    this.camera.setTarget(
      new Vector3(state.pos.x * 0.25 + this.peekX * 5, 1.3 + this.peekY * 2, state.pos.z + 6),
    );

    // The OPEN button pulses brighter when you're close enough to press it.
    const pulse = state.nearButton ? 0.7 + 0.3 * Math.sin(state.t * 8) : 0.5;
    this.buttonMat.emissiveColor = new Color3(pulse + 0.3, 0.1, 0.12);
    if (state.opened) this.button.scaling.y = 0.3; // pressed in
  }

  render(): void {
    this.scene.render();
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    this.scene.dispose();
  }
}
